import React, { useState, useMemo, useEffect, useRef } from 'react';
import Modal from '../common/Modal';
import { fieldStyles } from '../../config/formStyles';
import { styles } from '../../config/styles';
import {
  formatCurrency,
  formatTasaForDisplay,
  formatNumberForDisplay,
  parseNumberForState,
  parseVnForState,
  parseTasaForState,
  formatComitenteForDisplay,
} from '../../utils/formatters';
import {
  proposeAdjudicacion,
  montoAdjudicadoSugerido,
  prorratearAdjudicacion,
  monedaBucket,
  montoEnMonedaIntegracion,
  requiereTipoCambio,
  getTipoCambio,
} from '../../utils/licitacionesCalculator';
import { confirmarAdjudicacion } from '../../services/licitacionesService';

const cellStyle = { ...styles.td, padding: '0.5rem 0.75rem' };
const inputCellStyle = { ...fieldStyles.input, padding: '0.3rem 0.5rem', fontSize: '0.8rem', textAlign: 'right' };
const barStyle = {
  display: 'flex', gap: '1rem', alignItems: 'flex-end', flexWrap: 'wrap',
  padding: '0.75rem 1rem', marginBottom: '0.85rem',
  backgroundColor: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: '6px',
};

const REDONDEOS = [
  { value: 1, label: 'Sin redondeo' },
  { value: 1000, label: 'Múltiplos de 1.000' },
];

const toNumber = (value) => parseFloat(parseNumberForState(value)) || 0;
// El VN es entero: se parsea aparte para que "50.000.000" no se lea como 50.000,000.
const toVn = (value) => parseFloat(parseVnForState(value)) || 0;

/**
 * Modal de adjudicación de una especie. Dos formas de llegar a la tabla:
 *
 * - Por corte: se carga la tasa/precio de corte y el sistema propone qué
 *   órdenes adjudican (subasta holandesa).
 * - Manual: cuando el colocador informa directamente cuántos nominales recibió
 *   cada cliente. Arranca con la adjudicación completa y se ajusta a mano.
 *
 * En ambos casos se puede prorratear un VN total (o un % sobre lo solicitado)
 * entre las órdenes incluidas. Nada se escribe hasta "Confirmar".
 */
const AdjudicacionModal = ({ isOpen, onClose, onSaved, licitacion, especie, ordenes, userId, userName }) => {
  const [tasaCorteInput, setTasaCorteInput] = useState('');
  const [precioCorteInput, setPrecioCorteInput] = useState('');
  const [rows, setRows] = useState(null); // null = tabla aún no generada
  const [modoCarga, setModoCarga] = useState('');
  const [vnTotalInput, setVnTotalInput] = useState('');
  const [pctInput, setPctInput] = useState('');
  const [redondeo, setRedondeo] = useState(1);
  const [formError, setFormError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const didInit = useRef(false);

  const ordenesEspecie = useMemo(
    () => (ordenes || []).filter(o => o.especieId === especie?.id && o.estado !== 'Eliminada'),
    [ordenes, especie]
  );

  useEffect(() => {
    if (!isOpen) { didInit.current = false; return; }
    if (didInit.current) return;
    didInit.current = true;
    setFormError('');
    setIsSaving(false);
    setRows(null);
    setModoCarga('');
    setVnTotalInput('');
    setPctInput('');
    setRedondeo(1);
    setTasaCorteInput(especie?.tasaCorte ? formatTasaForDisplay(especie.tasaCorte) : '');
    setPrecioCorteInput(especie?.precioCorte ? formatNumberForDisplay(especie.precioCorte) : '');
  }, [isOpen, especie]);

  const especieConCorte = useMemo(() => ({
    ...especie,
    tasaCorte: parseFloat(parseTasaForState(tasaCorteInput)) || 0,
    precioCorte: toNumber(precioCorteInput),
  }), [especie, tasaCorteInput, precioCorteInput]);

  const buildRow = (orden, estadoAdjudicacion, vn) => ({
    ordenId: orden.id,
    orden,
    estadoAdjudicacion,
    vnAdjudicado: vn,
    montoAdjudicado: montoAdjudicadoSugerido(orden, especieConCorte, vn),
    vnInput: formatNumberForDisplay(vn, 0),
    montoInput: formatNumberForDisplay(montoAdjudicadoSugerido(orden, especieConCorte, vn)),
    montoTouched: false,
  });

  const handleProponer = () => {
    if (especie.modalidad === 'Tasa' && !(especieConCorte.tasaCorte > 0)) {
      setFormError('Cargá la tasa de corte para generar la propuesta.');
      return;
    }
    if (especie.modalidad === 'Precio' && !(especieConCorte.precioCorte > 0)) {
      setFormError('Cargá el precio de corte para generar la propuesta.');
      return;
    }
    setFormError('');
    const propuestas = proposeAdjudicacion(ordenesEspecie, especieConCorte);
    setRows(propuestas.map(p => {
      const orden = ordenesEspecie.find(o => o.id === p.ordenId);
      return {
        ...p,
        orden,
        vnInput: formatNumberForDisplay(p.vnAdjudicado, 0),
        montoInput: formatNumberForDisplay(p.montoAdjudicado),
        montoTouched: false,
      };
    }));
    setModoCarga('Corte');
  };

  // Carga manual: arranca en adjudicación completa (o en lo ya adjudicado si se
  // está re-adjudicando) y el usuario ajusta el VN de cada cliente.
  const handleCargaManual = () => {
    setFormError('');
    setRows(ordenesEspecie.map(orden => {
      const previo = parseFloat(orden.vnAdjudicado) || 0;
      const vn = previo > 0 ? previo : (parseFloat(orden.cantidadVN) || 0);
      return buildRow(orden, vn > 0 ? 'Adjudicada' : 'Rechazada', vn);
    }));
    setModoCarga('Manual');
  };

  const handleToggleAdjudica = (index) => {
    setRows(prev => prev.map((row, i) => {
      if (i !== index) return row;
      const adjudica = row.estadoAdjudicacion !== 'Adjudicada';
      const vn = adjudica ? (parseFloat(row.orden.cantidadVN) || 0) : 0;
      return buildRow(row.orden, adjudica ? 'Adjudicada' : 'Rechazada', vn);
    }));
  };

  // Al editar el VN se recalcula el monto sugerido, salvo que el usuario ya
  // haya pisado el monto a mano. VN en 0 = el cliente no adjudicó.
  const handleVnBlur = (index, value) => {
    setRows(prev => prev.map((row, i) => {
      if (i !== index) return row;
      const vn = toVn(value);
      const monto = row.montoTouched ? row.montoAdjudicado : montoAdjudicadoSugerido(row.orden, especieConCorte, vn);
      return {
        ...row,
        estadoAdjudicacion: vn > 0 ? 'Adjudicada' : 'Rechazada',
        vnAdjudicado: vn,
        montoAdjudicado: vn > 0 ? monto : 0,
        vnInput: formatNumberForDisplay(vn, 0),
        montoInput: formatNumberForDisplay(vn > 0 ? monto : 0),
      };
    }));
  };

  const handleMontoBlur = (index, value) => {
    setRows(prev => prev.map((row, i) => {
      if (i !== index) return row;
      const monto = toNumber(value);
      return { ...row, montoAdjudicado: monto, montoInput: formatNumberForDisplay(monto), montoTouched: true };
    }));
  };

  // Base del prorrateo: lo solicitado por las órdenes marcadas como adjudicadas.
  const vnSolicitadoIncluido = useMemo(() => (rows || [])
    .filter(r => r.estadoAdjudicacion === 'Adjudicada')
    .reduce((acc, r) => acc + (parseFloat(r.orden.cantidadVN) || 0), 0), [rows]);

  const handleVnTotalBlur = (value) => {
    const vnTotal = toVn(value);
    setVnTotalInput(vnTotal > 0 ? formatNumberForDisplay(vnTotal, 0) : '');
    setPctInput(vnTotal > 0 && vnSolicitadoIncluido > 0
      ? formatNumberForDisplay(vnTotal / vnSolicitadoIncluido * 100)
      : '');
  };

  const handlePctBlur = (value) => {
    const pct = toNumber(value);
    setPctInput(pct > 0 ? formatNumberForDisplay(pct) : '');
    setVnTotalInput(pct > 0 && vnSolicitadoIncluido > 0
      ? formatNumberForDisplay(vnSolicitadoIncluido * pct / 100, 0)
      : '');
  };

  // El prorrateo es una operación masiva: recalcula todos los montos, incluidos
  // los que se habían pisado a mano.
  const handleProrratear = () => {
    if (!rows) return;
    const vnTotal = toVn(vnTotalInput);
    if (!(vnTotal > 0)) {
      setFormError('Ingresá el VN total adjudicado (o el %) antes de prorratear.');
      return;
    }
    setFormError('');
    const asignaciones = prorratearAdjudicacion(
      rows.map(r => ({
        ordenId: r.ordenId,
        cantidadVN: parseFloat(r.orden.cantidadVN) || 0,
        incluida: r.estadoAdjudicacion === 'Adjudicada',
      })),
      { vnTotal, redondeo }
    );
    const porOrden = new Map(asignaciones.map(a => [a.ordenId, a.vnAdjudicado]));
    setRows(prev => prev.map(row => {
      const vn = porOrden.get(row.ordenId) || 0;
      return buildRow(row.orden, vn > 0 ? 'Adjudicada' : 'Rechazada', vn);
    }));
    setModoCarga('Prorrateo');
  };

  // Los montos de la tabla están en moneda de emisión; los totales por moneda se
  // expresan en lo que cada cliente integra, así que las especies en USD que se
  // integran en pesos pasan por el tipo de cambio de la especie.
  const totales = useMemo(() => {
    if (!rows) return null;
    const acc = rows.reduce((sum, row) => {
      sum.vnSolicitado += parseFloat(row.orden.cantidadVN) || 0;
      if (row.estadoAdjudicacion !== 'Adjudicada') return sum;
      sum.vn += row.vnAdjudicado;
      const { monto, sinTipoCambio } = montoEnMonedaIntegracion(
        row.montoAdjudicado, especie, row.orden.monedaIntegracion
      );
      sum.sinTipoCambio = sum.sinTipoCambio || sinTipoCambio;
      if (monedaBucket(row.orden.monedaIntegracion) === 'ARS') sum.montoARS += monto;
      else sum.montoUSD += monto;
      sum.cantidad += 1;
      return sum;
    }, { vn: 0, vnSolicitado: 0, montoARS: 0, montoUSD: 0, cantidad: 0, sinTipoCambio: false });
    acc.pct = acc.vnSolicitado > 0 ? acc.vn / acc.vnSolicitado * 100 : 0;
    return acc;
  }, [rows, especie]);

  // Descuadre entre el VN total pedido para prorratear y lo efectivamente
  // asignado (pasa cuando el total supera lo solicitado).
  const vnObjetivo = toVn(vnTotalInput);
  const descuadre = !!totales && vnObjetivo > 0 && Math.abs(totales.vn - vnObjetivo) > 0.5;

  const handleConfirmar = async () => {
    if (!rows) return;
    setIsSaving(true);
    setFormError('');
    const algunaAdjudicada = rows.some(r => r.estadoAdjudicacion === 'Adjudicada');
    const especiesActualizadas = (licitacion.especies || []).map(esp =>
      esp.id === especie.id
        ? {
          ...esp,
          tasaCorte: especieConCorte.tasaCorte,
          precioCorte: especieConCorte.precioCorte,
          estado: algunaAdjudicada ? 'Adjudicada' : 'Desierta',
          adjudicadoPorId: userId || '',
          adjudicadoPorNombre: userName || '',
          // serverTimestamp() no funciona dentro de elementos de un array.
          adjudicadoAt: new Date().toISOString(),
          adjudicadoModo: modoCarga || 'Manual',
        }
        : esp
    );
    try {
      await confirmarAdjudicacion({
        licitacionId: licitacion.id,
        especies: especiesActualizadas,
        propuestas: rows.map(({ ordenId, estadoAdjudicacion, vnAdjudicado, montoAdjudicado }) =>
          ({ ordenId, estadoAdjudicacion, vnAdjudicado, montoAdjudicado })),
      });
      onSaved?.();
      onClose?.();
    } catch (error) {
      console.error('Error confirmando la adjudicación:', error);
      setFormError('No se pudo confirmar la adjudicación. Intentá de nuevo.');
    } finally {
      setIsSaving(false);
    }
  };

  if (!especie) return null;

  return (
    <Modal
      isOpen={isOpen}
      title={`Adjudicar — ${especie.denominacion}${especie.clase ? ` (${especie.clase})` : ''}`}
      maxWidth="1050px"
      error={formError}
      footer={<>
        <button type="button" onClick={onClose} style={{ ...fieldStyles.button, ...fieldStyles.secondaryButton }}>Cancelar</button>
        <button type="button" onClick={handleConfirmar} style={fieldStyles.button} disabled={!rows || isSaving}>
          {isSaving ? 'Confirmando...' : 'Confirmar Adjudicación'}
        </button>
      </>}
    >
      <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'flex-end', marginBottom: '1rem', flexWrap: 'wrap' }}>
        {especie.modalidad === 'Tasa' && (
          <div style={{ width: '160px' }}>
            <label style={fieldStyles.label}>Tasa de corte (%)</label>
            <input
              type="text" inputMode="decimal" value={tasaCorteInput}
              onChange={(e) => setTasaCorteInput(e.target.value)}
              onBlur={(e) => setTasaCorteInput(formatTasaForDisplay(parseTasaForState(e.target.value)))}
              style={fieldStyles.input}
            />
          </div>
        )}
        <div style={{ width: '190px' }}>
          <label style={fieldStyles.label}>
            Precio de corte (c/1000){especie.modalidad === 'Tasa' ? ' — opcional' : ''}
          </label>
          <input
            type="text" inputMode="decimal" value={precioCorteInput}
            onChange={(e) => setPrecioCorteInput(e.target.value)}
            onBlur={(e) => setPrecioCorteInput(formatNumberForDisplay(parseNumberForState(e.target.value)))}
            style={fieldStyles.input}
            placeholder={especie.modalidad === 'Tasa' ? 'Vacío = a la par' : ''}
          />
        </div>
        <button type="button" onClick={handleProponer} style={{ ...fieldStyles.button, padding: '0.55rem 1.2rem' }}>
          Proponer por corte
        </button>
        <button
          type="button"
          onClick={handleCargaManual}
          style={{ ...fieldStyles.button, ...fieldStyles.secondaryButton, padding: '0.55rem 1.2rem' }}
        >
          Carga manual
        </button>
        {requiereTipoCambio(especie) && (
          <div style={{
            fontSize: '0.75rem', maxWidth: '260px', lineHeight: 1.4, paddingBottom: '0.5rem',
            color: getTipoCambio(especie) > 0 ? '#6B7281' : '#B45309',
            fontWeight: getTipoCambio(especie) > 0 ? 400 : 600,
          }}>
            {getTipoCambio(especie) > 0
              ? `Los montos de la tabla van en USD; se integran en pesos al TC ${formatNumberForDisplay(getTipoCambio(especie))}.`
              : 'Se integra en pesos y la especie no tiene tipo de cambio cargado: los pesos a fondear no se pueden calcular. Cargalo editando la licitación.'}
          </div>
        )}
      </div>

      {!rows && (
        <p style={{ color: '#6B7281', fontSize: '0.875rem', lineHeight: 1.6 }}>
          <strong>Proponer por corte</strong>: cargá el corte y el sistema resuelve qué órdenes adjudican
          (las no competitivas siempre; las competitivas según {especie.modalidad === 'Tasa' ? 'tasa ofertada ≤ corte' : 'precio ofertado ≥ corte'}).
          <br />
          <strong>Carga manual</strong>: si el colocador ya informó cuántos nominales recibió cada cliente,
          entrá por acá y tipealos directamente — no hace falta cargar el corte. Un VN en 0 marca la orden como rechazada.
        </p>
      )}

      {rows && (
        <>
          <div style={barStyle}>
            <div style={{ width: '190px' }}>
              <label style={fieldStyles.label}>VN total adjudicado</label>
              <input
                type="text" inputMode="numeric" value={vnTotalInput}
                onChange={(e) => setVnTotalInput(e.target.value)}
                onFocus={(e) => setVnTotalInput(parseVnForState(e.target.value))}
                onBlur={(e) => handleVnTotalBlur(e.target.value)}
                style={fieldStyles.input}
                placeholder="Lo que dio el colocador"
              />
            </div>
            <div style={{ width: '130px' }}>
              <label style={fieldStyles.label}>% s/solicitado</label>
              <input
                type="text" inputMode="decimal" value={pctInput}
                onChange={(e) => setPctInput(e.target.value)}
                onBlur={(e) => handlePctBlur(e.target.value)}
                style={fieldStyles.input}
                placeholder="Ej: 60"
              />
            </div>
            <div style={{ width: '175px' }}>
              <label style={fieldStyles.label}>Redondeo</label>
              <select value={redondeo} onChange={(e) => setRedondeo(parseInt(e.target.value, 10))} style={fieldStyles.select}>
                {REDONDEOS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
              </select>
            </div>
            <button type="button" onClick={handleProrratear} style={{ ...fieldStyles.button, padding: '0.55rem 1.2rem' }}>
              Prorratear
            </button>
            <span style={{ fontSize: '0.75rem', color: '#6B7281', maxWidth: '260px', lineHeight: 1.4 }}>
              Reparte el total entre las órdenes marcadas, proporcional a lo solicitado. Pisa los montos editados a mano.
            </span>
          </div>

          <div style={{ ...styles.tableContainer, boxShadow: 'none', border: '1px solid #E5E7EB' }}>
            <table style={{ ...styles.table, minWidth: '900px' }}>
              <thead>
                <tr>
                  <th style={styles.th}>Adjudica</th>
                  <th style={styles.th}>Comitente</th>
                  <th style={styles.th}>Cliente</th>
                  <th style={styles.th}>Tramo</th>
                  <th style={styles.th}>{especie.modalidad === 'Tasa' ? 'Tasa Ofertada' : 'Precio Ofertado'}</th>
                  <th style={styles.th}>VN Solicitado</th>
                  <th style={styles.th}>Moneda</th>
                  <th style={styles.th}>VN Adjudicado</th>
                  <th style={styles.th}>%</th>
                  <th style={styles.th}>
                    Monto a Liquidar{especie.monedaEmision === 'USD' ? ' (USD)' : ''}
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => {
                  const adjudica = row.estadoAdjudicacion === 'Adjudicada';
                  const pedido = parseFloat(row.orden.cantidadVN) || 0;
                  const pctFila = pedido > 0 ? row.vnAdjudicado / pedido * 100 : 0;
                  return (
                    <tr key={row.ordenId} style={{ backgroundColor: adjudica ? styles.colors.green : styles.colors.red }}>
                      <td style={cellStyle}>
                        <input type="checkbox" checked={adjudica} onChange={() => handleToggleAdjudica(index)} style={{ cursor: 'pointer' }} />
                      </td>
                      <td style={cellStyle}>{formatComitenteForDisplay(row.orden.nroComitente)}</td>
                      <td style={{ ...cellStyle, textAlign: 'left' }}>{row.orden.cliente}</td>
                      <td style={cellStyle}>{row.orden.tramo === 'No Competitivo' ? 'NC' : 'Comp.'}</td>
                      <td style={cellStyle}>
                        {row.orden.tramo === 'No Competitivo'
                          ? '-'
                          : (especie.modalidad === 'Tasa'
                            ? formatTasaForDisplay(row.orden.tasaOfertada)
                            : formatNumberForDisplay(row.orden.precioOfertado))}
                      </td>
                      <td style={cellStyle}>{formatNumberForDisplay(pedido, 0)}</td>
                      <td style={cellStyle}>{row.orden.monedaIntegracion}</td>
                      <td style={{ ...cellStyle, width: '130px' }}>
                        <input
                          type="text" inputMode="numeric" value={row.vnInput}
                          onChange={(e) => setRows(prev => prev.map((r, i) => i === index ? { ...r, vnInput: e.target.value } : r))}
                          onFocus={(e) => setRows(prev => prev.map((r, i) => i === index ? { ...r, vnInput: parseVnForState(e.target.value) } : r))}
                          onBlur={(e) => handleVnBlur(index, e.target.value)}
                          style={inputCellStyle}
                          title="0 = el cliente no adjudicó"
                        />
                      </td>
                      <td style={{ ...cellStyle, fontWeight: 600 }}>
                        {adjudica ? `${formatNumberForDisplay(pctFila, 1)}%` : '-'}
                      </td>
                      <td style={{ ...cellStyle, width: '150px' }}>
                        <input
                          type="text" inputMode="decimal" value={row.montoInput} disabled={!adjudica}
                          onChange={(e) => setRows(prev => prev.map((r, i) => i === index ? { ...r, montoInput: e.target.value } : r))}
                          onBlur={(e) => handleMontoBlur(index, e.target.value)}
                          style={inputCellStyle}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {totales && (
            <div style={{
              display: 'flex', gap: '1.5rem', marginTop: '0.85rem', padding: '0.75rem 1rem',
              backgroundColor: descuadre ? styles.colors.red : '#F9FAFB',
              border: `1px solid ${descuadre ? '#FCA5A5' : '#E5E7EB'}`, borderRadius: '6px',
              fontSize: '0.875rem', fontWeight: 600, color: styles.colors.primary, flexWrap: 'wrap',
            }}>
              <span>Órdenes adjudicadas: {totales.cantidad} de {rows.length}</span>
              <span>VN solicitado: {formatNumberForDisplay(totales.vnSolicitado, 0)}</span>
              <span>VN adjudicado: {formatNumberForDisplay(totales.vn, 0)} ({formatNumberForDisplay(totales.pct, 1)}%)</span>
              <span>
                A liquidar ARS: {totales.sinTipoCambio
                  ? <span style={{ color: '#B45309' }}>falta tipo de cambio</span>
                  : formatCurrency(totales.montoARS, 'Pesos')}
              </span>
              <span>A liquidar USD: {formatCurrency(totales.montoUSD, 'USD')}</span>
              {descuadre && (
                <span style={{ color: styles.colors.redText }}>
                  No cuadra con el VN total pedido ({formatNumberForDisplay(vnObjetivo, 0)}):
                  difieren {formatNumberForDisplay(Math.abs(totales.vn - vnObjetivo), 0)}
                </span>
              )}
            </div>
          )}
        </>
      )}
    </Modal>
  );
};

export default AdjudicacionModal;
